/**
 * @fileoverview Sentry Integration Utilities
 *
 * Provides Sentry error tracking initialization and configuration.
 * Integrates with environment variables for flexible configuration.
 *
 * @module hooks/useSentry
 */

import * as Sentry from '@sentry/react';
import { getEnvironmentConfig } from './useEnvironment';

import { createLogger } from '../logger';

const logger = createLogger('Lib - useSentry');

/**
 * Initializes Sentry error tracking with environment-based configuration
 *
 * Configures Sentry SDK with:
 * - DSN from environment variables
 * - Browser performance tracing
 * - Configurable trace sampling rate
 * - PII (Personally Identifiable Information) handling
 * - Trace propagation for distributed tracing
 *
 * @returns {boolean} True if Sentry was successfully initialized, false otherwise
 *
 * @example
 * ```tsx
 * // In your app initialization
 * const sentryInitialized = initSentry();
 * if (sentryInitialized) {
 *   logger.info('Sentry error tracking enabled');
 * }
 * ```
 *
 * @remarks
 * Configuration is read from environment variables:
 * - VITE_SENTRY_DSN: Sentry Data Source Name (required)
 * - VITE_SENTRY_SEND_PII: Enable/disable sending PII (default: false)
 * - VITE_SENTRY_TRACES_SAMPLE_RATE: Percentage of transactions to trace (default: 1.0)
 * - VITE_SENTRY_TRACE_PROPAGATION_TARGETS: URLs for distributed tracing
 * - VITE_SENTRY_ENABLE_LOGS: Enable/disable log integration
 */
export function initSentry(): boolean {
  const env =
    getEnvironmentConfig().loggingVars && getEnvironmentConfig().sentryVars;

  Sentry.init({
    dsn:
      env.SENTRY_DSN ||
      'https://08e2f6625018d0d1c6da2c31b37df1fa@o4510611331153920.ingest.de.sentry.io/4510611333775440',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: env.SENTRY_SEND_PII,
    integrations: [Sentry.browserTracingIntegration()],
    // Tracing
    tracesSampleRate: parseFloat(env.SENTRY_TRACES_SAMPLE_RATE || '1.0'), //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    //   tracePropagationTargets: ['localhost', /^https:\/\/www\.xanectech\.com\/api/],
    tracePropagationTargets: env.SENTRY_TRACE_PROPAGATION_TARGETS
      ? env.SENTRY_TRACE_PROPAGATION_TARGETS.split(',')
      : ['localhost', 'https://www.xanectech.com/api'],
    // Enable logs to be sent to Sentry
    enableLogs: env.SENTRY_ENABLE_LOGS,
  });

  return Sentry.isInitialized();
}
